import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import CompanyTimelinePanel from "bundles/Internal/components/common/CompanyTimelinePanel";
import CompanyInsightsPanel from "bundles/Internal/components/common/CompanyInsightsPanel";
import FeatureAdoptionPanel from "bundles/Internal/components/company/FeatureAdoptionPanel";
import FeatureActivityPanel from "bundles/Internal/components/common/FeatureActivityPanel";
import EngagementPanel from "bundles/Internal/components/common/EngagementPanel";
import TimeSpentPanel from "bundles/Internal/components/common/TimeSpentPanel";
import AddCreditsDialog from "bundles/Internal/components/common/AddCreditsDialog";
import {
  ArrowLeft,
  Building2,
  Briefcase,
  Activity,
  Users,
  FileText,
  CreditCard,
  MapPin,
  Globe,
  MoreVertical,
  Clock,
} from "lucide-react";
import { INDUSTRY_COLOR } from "bundles/Internal/components/company/CompanyTable";
import { TEAM_SIZE_LABELS } from "bundles/Internal/components/company/CompanyTableFilters";

import DateTimeSelect from "bundles/Internal/components/common/DateTimeSelect";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import CompanyDetailsTab from "bundles/Internal/components/company/CompanyDetailsTab";
import CompanyCreditsTab from "bundles/Internal/components/company/CompanyCreditsTab";
import CompanyJobApplicationsTab from "bundles/Internal/components/company/CompanyJobApplicationsTab";
import CompanyJobOffersTab from "bundles/Internal/components/company/CompanyJobOffersTab";
import CompanyRecruitersTab from "bundles/Internal/components/company/CompanyRecruitersTab";
import FollowButton from "bundles/Internal/components/common/FollowButton";

export const DATE_KEYS = new Set(["created_at", "deleted_at"]);

export const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

export const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

export const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

export const OFFER_STATUS_COLOR = {
  online: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  draft: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
  closed: { border: "#757575", bg: "rgba(117,117,117,0.10)", text: "#757575" },
  expired: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
};

export const CREDIT_STATUS_COLOR = {
  pending: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  available: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  activated: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  expired: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
};

export const APPLICATION_STATUS_COLOR = {
  new: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  accepted: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  denied: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  pending: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

export const arrayToText = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return "None";
  return arr.join(", ");
};

export const locationsToText = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) return "None";
  return locations
    .map((l) => {
      if (typeof l === "string") return l;
      if (l && typeof l === "object") {
        return [l.city, l.country].filter(Boolean).join(", ") || JSON.stringify(l);
      }
      return String(l);
    })
    .join(" · ");
};

export const formatCompanyCell = (company, key) => {
  const value = company?.[key];
  if (value === null || value === undefined || value === "") return "";

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (key === "team_size") return TEAM_SIZE_LABELS[value] || value;

  if (key === "company_industry") {
    return pill(value, INDUSTRY_COLOR[value] || INDUSTRY_COLOR.industry);
  }

  if (key === "social_links" || key === "feature_flags" || key === "external_tools") {
    if (!value || (typeof value === "object" && Object.keys(value).length === 0)) return "";
    return JSON.stringify(value);
  }

  if (key === "recruitment_process" || key === "awards_data") {
    if (!Array.isArray(value) || value.length === 0) return "";
    return `${value.length} entr${value.length === 1 ? "y" : "ies"}`;
  }

  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const sectionPaperSx = {
  borderRadius: "0.75rem",
  overflow: "hidden",
  border: "1px solid hsl(140 8% 88%)",
  p: 4,
};

const sectionTitleSx = {
  color: "hsl(150 30% 12%)",
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const KeyValueTile = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        color: "hsl(150 10% 46%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 600,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: "hsl(150 30% 12%)",
        mt: 0.5,
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      {value}
    </Box>
  </Box>
);

export const tableHeadCellSx = {
  backgroundColor: "transparent",
  borderBottom: "1px solid hsl(140 8% 88%)",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "hsl(150 10% 46%)",
  py: "10px",
  px: "16px",
  whiteSpace: "nowrap",
};

export const tableBodyCellSx = {
  whiteSpace: "nowrap",
  py: "14px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  maxWidth: 240,
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "1px solid hsl(140 8% 88%)",
};

const TAB_KEYS = ["details", "activity", "credits", "job_applications", "job_offers", "recruiters", "time_spent"];

function CompanyShowPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const activeTab = Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0);
  const setActiveTab = (v) => setSearchParams({ tab: TAB_KEYS[v] }, { replace: true, state: location.state });
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/companies"));
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);
  const [insightsParams, setInsightsParams] = useState({ period: "month", startDate: null, endDate: null });
  const insightsLoadedRef = useRef(false);
  const [creditsForm, setCreditsForm] = useState({
    package: "business",
    licence_credits: 3,
    cv_access_credit: false,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [addCreditsSimpleOpen, setAddCreditsSimpleOpen] = useState(false);
  const [editCredit, setEditCredit] = useState(null);
  const [editCreditForm, setEditCreditForm] = useState({ status: "", expire_at: "" });
  const [deleteCredit, setDeleteCredit] = useState(null);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    sh.get(`/internal/companies/${id}`)
      .then((res) => {
        setCompany(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to load company");
      });
  }, [id]);

  useEffect(() => {
    if (!id || TAB_KEYS[activeTab] !== "activity") return;
    if (insightsParams.period === "custom" && (!insightsParams.startDate || !insightsParams.endDate)) return;

    const params = { period: insightsParams.period };
    if (insightsParams.period === "custom") {
      params.start_date = insightsParams.startDate;
      params.end_date = insightsParams.endDate;
    }

    if (insightsLoadedRef.current) setInsightsRefreshing(true);
    else setInsightsLoading(true);

    sh.get(`/internal/companies/${id}/insights`, { params })
      .then((res) => {
        setInsights(res.data);
        insightsLoadedRef.current = true;
      })
      .catch(() => {})
      .finally(() => {
        setInsightsLoading(false);
        setInsightsRefreshing(false);
      });
  }, [id, activeTab, insightsParams]);

  const title = useMemo(() => {
    if (!company) return "";
    return company.name || `Company #${company.id}`;
  }, [company]);

  const getInitials = () => {
    if (!company?.name) return "?";
    const parts = company.name.trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase() || "?";
  };

  const handleAddCredits = async () => {
    setActionLoading(true);
    try {
      const response = await sh.post(`/internal/companies/${id}/grant_package`, creditsForm);
      setAddCreditsOpen(false);
      setCreditsForm({
        package: "business",
        licence_credits: 3,
        cv_access_credit: false,
      });
      const res = await sh.get(`/internal/companies/${id}`);
      setCompany(res.data);

      if (response.data.warning) {
        enqueueSnackbar(response.data.warning, { variant: "warning" });
      } else if (response.data.success) {
        enqueueSnackbar(response.data.success, { variant: "success" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to grant package", { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCreditsSimple = async (form) => {
    setActionLoading(true);
    try {
      const response = await sh.post(`/internal/companies/${id}/add_credits`, form);
      setAddCreditsSimpleOpen(false);
      setCompany((prev) => ({ ...prev, credits: response.data.credits }));

      if (response.data.success) {
        enqueueSnackbar(response.data.success, { variant: "success" });
      }
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || "Failed to add credits";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEditCredit = useCallback((credit) => {
    setEditCredit(credit);
    setEditCreditForm({
      status: credit.status || "",
      expire_at: credit.expire_at ? credit.expire_at.slice(0, 10) : "",
    });
  }, []);

  const handleUpdateCredit = async () => {
    if (!editCredit) return;
    setActionLoading(true);
    try {
      const response = await sh.patch(
        `/internal/companies/${id}/credits/${editCredit.id}`,
        editCreditForm
      );
      setEditCredit(null);
      setCompany((prev) => ({ ...prev, credits: response.data.credits }));
      enqueueSnackbar(response.data.success || "Credit updated", { variant: "success" });
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || "Failed to update credit";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCredit = async () => {
    if (!deleteCredit) return;
    setActionLoading(true);
    try {
      const response = await sh.delete(
        `/internal/companies/${id}/credits/${deleteCredit.id}`
      );
      setDeleteCredit(null);
      setCompany((prev) => ({ ...prev, credits: response.data.credits }));
      enqueueSnackbar(response.data.success || "Credit deleted", { variant: "success" });
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0] || "Failed to delete credit";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const wideTab = TAB_KEYS[activeTab] !== "details";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 72px)",
        backgroundColor: "hsl(140 10% 95%)",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minWidth: 0 }}
            >
              <Button
                onClick={goBack}
                disableRipple
                sx={{
                  minWidth: 0,
                  p: 0.75,
                  color: "hsl(150 10% 46%)",
                  "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
                }}
              >
                <ArrowLeft size={20} />
              </Button>

              {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={140} height={20} />
                    <Skeleton width={200} height={14} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={company?.logo_url || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      backgroundColor: "hsl(150 38% 17%)",
                      color: "hsl(80 97% 72%)",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 16, sm: 18 },
                          fontWeight: 600,
                          color: "hsl(150 30% 12%)",
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {title}
                      </Typography>
                      {company?.company_industry &&
                        pill(
                          company.company_industry,
                          INDUSTRY_COLOR[company.company_industry] || INDUSTRY_COLOR.industry
                        )}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 0.25, flexWrap: "wrap", rowGap: 0.25 }}
                    >
                      {(company?.city || company?.country) && (
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                          }}
                        >
                          <MapPin size={13} />
                          {[company.city, company.country].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                      {company?.domain_name && (
                        <Typography
                          component="a"
                          href={`https://${company.domain_name.replace(/^https?:\/\//, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                            textDecoration: "none",
                            "&:hover": { color: "hsl(150 30% 12%)" },
                          }}
                        >
                          <Globe size={13} />
                          {company.domain_name}
                        </Typography>
                      )}
                      {company?.team_size && (
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                          }}
                        >
                          <Users size={13} />
                          {TEAM_SIZE_LABELS[company.team_size] || company.team_size}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}

              {!loading && company?.company_metrics && (
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}>
                  {company?.id && (
                    <FollowButton
                      followableType="Company"
                      followableId={company.id}
                      followed={company.followed}
                      onChange={(next) => setCompany((prev) => (prev ? { ...prev, followed: next } : prev))}
                    />
                  )}
                  <Chip
                    label={`Credits left for the company: ${company.company_metrics.available_licence ?? 0}/${company.company_metrics.total_licence ?? 0}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(25,118,210,0.10)",
                      color: "#1976D2",
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      fontSize: 11,
                      fontWeight: 600,
                      borderRadius: "8px",
                      height: 24,
                    }}
                  />
                  {company.company_metrics.usage_delta_pct != null && (
                    <Chip
                      label={`Recruiter logins: ${
                        company.company_metrics.usage_delta_pct >= 0
                          ? `↑${Math.abs(company.company_metrics.usage_delta_pct)}%`
                          : `↓${Math.abs(company.company_metrics.usage_delta_pct)}%`
                      } vs last month`}
                      size="small"
                      sx={{
                        backgroundColor:
                          company.company_metrics.usage_delta_pct >= 0
                            ? "rgba(7,147,68,0.10)"
                            : "rgba(211,47,47,0.10)",
                        color:
                          company.company_metrics.usage_delta_pct >= 0 ? "#079344" : "#D32F2F",
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: "8px",
                        height: 24,
                      }}
                    />
                  )}
                </Box>
              )}

              {!loading && (
                <Button
                  endIcon={<MoreVertical size={18} />}
                  onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                  disabled={loading || !company?.id || actionLoading}
                  disableRipple
                  size="small"
                  sx={{
                    color: "hsl(150 30% 12%)",
                    fontWeight: 600,
                    fontSize: 13,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    textTransform: "none",
                    borderRadius: "0.5rem",
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "#fff",
                    border: "1px solid hsl(140 8% 88%)",
                    transition: "opacity 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#fff",
                      opacity: 0.8,
                    },
                  }}
                >
                  Actions
                </Button>
              )}

              <Menu
                disableScrollLock
                anchorEl={actionsAnchorEl}
                open={actionsOpen}
                onClose={() => setActionsAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setActionsAnchorEl(null);
                    setAddCreditsOpen(true);
                  }}
                >
                  Grant Package
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setActionsAnchorEl(null);
                    setAddCreditsSimpleOpen(true);
                  }}
                >
                  Add Credits
                </MenuItem>
              </Menu>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Page Content */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={wideTab ? 12 : 8}>
            <Box sx={{ borderBottom: "1px solid hsl(140 8% 88%)", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    minHeight: 36,
                    py: 0.5,
                    px: 1.5,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(150 10% 46%)",
                    "&.Mui-selected": { color: "hsl(150 38% 17%)" },
                  },
                  "& .MuiTab-iconWrapper": { fontSize: 16 },
                  "& .MuiTabs-indicator": { backgroundColor: "hsl(150 38% 17%)" },
                }}
              >
                <Tab icon={<Building2 size={16} />} iconPosition="start" label="Details" />
                <Tab icon={<Activity size={16} />} iconPosition="start" label="Activity" />
                <Tab
                  icon={<CreditCard size={16} />}
                  iconPosition="start"
                  label={`Credits${company?.credits?.length ? ` (${company.credits.length})` : ""}`}
                />
                <Tab
                  icon={<FileText size={16} />}
                  iconPosition="start"
                  label={`Job Applications${
                    company?.job_applications?.length ? ` (${company.job_applications.length})` : ""
                  }`}
                />
                <Tab
                  icon={<Briefcase size={16} />}
                  iconPosition="start"
                  label={`Job Offers${
                    company?.job_offers?.length ? ` (${company.job_offers.length})` : ""
                  }`}
                />
                <Tab
                  icon={<Users size={16} />}
                  iconPosition="start"
                  label={`Recruiters${
                    company?.recruiters?.length ? ` (${company.recruiters.length})` : ""
                  }`}
                />
                <Tab icon={<Clock size={16} />} iconPosition="start" label="Time Spent" />
              </Tabs>
            </Box>

            <Paper elevation={0} sx={{ ...sectionPaperSx, ...(TAB_KEYS[activeTab] !== "details" && { p: 0 }) }}>
              {error && (
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: "hsla(0, 72%, 51%, 0.08)",
                    border: "1px solid hsla(0, 72%, 51%, 0.2)",
                    borderRadius: "calc(0.75rem - 2px)",
                  }}
                >
                  <Typography sx={{ color: "hsl(0 72% 51%)", fontSize: 14 }}>{error}</Typography>
                </Box>
              )}

              {TAB_KEYS[activeTab] === "details" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <Building2 size={20} color="hsl(150 38% 17%)" />
                    Company Details
                  </Typography>
                  <CompanyDetailsTab company={company} loading={loading} />
                </>
              )}

              {TAB_KEYS[activeTab] === "activity" && (
                <>
                  <CompanyInsightsPanel
                    companyMetrics={insights}
                    loading={insightsLoading}
                    refreshing={insightsRefreshing}
                    period={insightsParams.period}
                    customRange={{ startDate: insightsParams.startDate, endDate: insightsParams.endDate }}
                    onPeriodChange={(p) => setInsightsParams((s) => ({ ...s, period: p }))}
                    onCustomRangeChange={(r) => setInsightsParams((s) => ({ ...s, startDate: r.startDate, endDate: r.endDate }))}
                    onOpenJobApplications={() => setActiveTab(TAB_KEYS.indexOf("job_applications"))}
                    featureAdoption={<FeatureAdoptionPanel companyId={id} />}
                    activityLog={<FeatureActivityPanel scope="company" companyId={id} />}
                    timeOnPlatform={<EngagementPanel scope="company" companyId={id} period={insightsParams.period} customRange={{ startDate: insightsParams.startDate, endDate: insightsParams.endDate }} />}
                  />
                </>
              )}


              {TAB_KEYS[activeTab] === "credits" && (
                <CompanyCreditsTab credits={company?.credits} loading={loading} onEditCredit={handleOpenEditCredit} onDeleteCredit={setDeleteCredit} />
              )}

              {TAB_KEYS[activeTab] === "job_applications" && (
                <CompanyJobApplicationsTab
                  jobApplications={company?.job_applications}
                  loading={loading}
                />
              )}

              {TAB_KEYS[activeTab] === "job_offers" && (
                <CompanyJobOffersTab jobOffers={company?.job_offers} loading={loading} />
              )}

              {TAB_KEYS[activeTab] === "recruiters" && (
                <CompanyRecruitersTab recruiters={company?.recruiters} loading={loading} />
              )}

              {TAB_KEYS[activeTab] === "time_spent" && (
                <TimeSpentPanel companyId={id} />
              )}
            </Paper>
          </Grid>

          {!wideTab && (
            <Grid item xs={12} lg={4}>
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: { lg: 80 },
                }}
              >
                <CompanyTimelinePanel companyId={id} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Grant Package Dialog */}
      <Dialog
        open={addCreditsOpen}
        onClose={() => setAddCreditsOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px", minWidth: 420, maxWidth: 480 } }}
      >
        <DialogTitle sx={{
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontWeight: 700,
          fontSize: 18,
          color: "hsl(150 30% 12%)",
          pt: 3,
          px: 3,
          pb: 1
        }}>
          Grant credits
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(150 10% 46%)",
              mb: 0.75,
              textTransform: "uppercase",
              letterSpacing: "0.04em"
            }}>
              Package
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={creditsForm.package}
              onChange={(e) => {
                const newPackage = e.target.value;
                let credits = 3;
                if (newPackage === "starter") credits = 1;
                else if (newPackage === "premium") credits = 5;
                setCreditsForm({
                  ...creditsForm,
                  package: newPackage,
                  licence_credits: credits,
                });
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "12px",
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
                  "&.Mui-focused": { backgroundColor: "#fff" },
                },
              }}
            >
              <MenuItem value="starter" sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                Starter
              </MenuItem>
              <MenuItem value="business" sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                Business
              </MenuItem>
              <MenuItem value="premium" sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                Premium
              </MenuItem>
            </TextField>
          </Box>

          <Box>
            <Typography sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(150 10% 46%)",
              mb: 0.75,
              textTransform: "uppercase",
              letterSpacing: "0.04em"
            }}>
              Licence credits
            </Typography>
            <Box
              component="input"
              type="number"
              value={creditsForm.licence_credits}
              onChange={(e) => setCreditsForm({ ...creditsForm, licence_credits: parseInt(e.target.value) || 1 })}
              min="1"
              sx={{
                width: "100%",
                height: 40,
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "hsl(140 10% 95%)",
                px: 1.5,
                py: 1,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                outline: "none",
                boxSizing: "border-box",
                "&:focus": {
                  borderColor: "hsl(150 38% 17%)",
                  backgroundColor: "#fff"
                },
              }}
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={creditsForm.cv_access_credit}
                  onChange={(e) => setCreditsForm({ ...creditsForm, cv_access_credit: e.target.checked })}
                  sx={{
                    color: "hsl(140 8% 88%)",
                  }}
                />
              }
              label={
                <Typography sx={{
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)"
                }}>
                  Include a CV access credit?
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAddCreditsOpen(false)}
            disabled={actionLoading}
            sx={{
              height: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 14,
              color: "hsl(150 10% 46%)",
              "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleAddCredits}
            disabled={actionLoading}
            sx={{
              height: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              boxShadow: "none",
              "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)", boxShadow: "none" },
              "&.Mui-disabled": {
                opacity: 0.5,
                backgroundColor: "hsl(150 38% 17%)",
                color: "hsl(80 97% 72%)"
              },
            }}
          >
            {actionLoading ? "Granting..." : "Grant credits"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Credits Dialog */}
      <AddCreditsDialog
        open={addCreditsSimpleOpen}
        onClose={() => setAddCreditsSimpleOpen(false)}
        onSubmit={handleAddCreditsSimple}
        loading={actionLoading}
      />

      {/* Edit Credit Dialog */}
      <Dialog
        open={Boolean(editCredit)}
        onClose={() => setEditCredit(null)}
        PaperProps={{ sx: { borderRadius: "16px", minWidth: 420, maxWidth: 480 } }}
      >
        <DialogTitle sx={{
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontWeight: 700,
          fontSize: 18,
          color: "hsl(150 30% 12%)",
          pt: 3,
          px: 3,
          pb: 1
        }}>
          Edit Credit #{editCredit?.id}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(150 10% 46%)",
              mb: 0.75,
              textTransform: "uppercase",
              letterSpacing: "0.04em"
            }}>
              Type
            </Typography>
            <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
              {editCredit?.credit_type?.replace(/_/g, " ") || "None"}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(150 10% 46%)",
              mb: 0.75,
              textTransform: "uppercase",
              letterSpacing: "0.04em"
            }}>
              Status
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={editCreditForm.status}
              onChange={(e) => setEditCreditForm((prev) => ({ ...prev, status: e.target.value }))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "12px",
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
                  "&.Mui-focused": { backgroundColor: "#fff" },
                },
              }}
            >
              {["pending", "available", "activated", "expired"].map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(150 10% 46%)",
              mb: 0.75,
              textTransform: "uppercase",
              letterSpacing: "0.04em"
            }}>
              Expiry date
            </Typography>
            <DateTimeSelect
              dateOnly
              value={editCreditForm.expire_at}
              onChange={(val) => setEditCreditForm((prev) => ({ ...prev, expire_at: val }))}
              placeholder="Pick an expiry date…"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setEditCredit(null)}
            disabled={actionLoading}
            sx={{
              height: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 14,
              color: "hsl(150 10% 46%)",
              "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleUpdateCredit}
            disabled={actionLoading}
            sx={{
              height: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              boxShadow: "none",
              "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)", boxShadow: "none" },
              "&.Mui-disabled": {
                opacity: 0.5,
                backgroundColor: "hsl(150 38% 17%)",
                color: "hsl(80 97% 72%)"
              },
            }}
          >
            {actionLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={Boolean(deleteCredit)}
        onClose={() => setDeleteCredit(null)}
        onConfirm={handleDeleteCredit}
        title={`Delete Credit #${deleteCredit?.id}?`}
        description="This action cannot be undone. The credit will be permanently removed."
        confirmLabel="Delete"
        destructive
      />
    </Box>
  );
}

export default CompanyShowPage;
